import React from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { ProtectedLayout } from './layout/ProtectedLayout';
import { CustomerFormRoute, CustomersPage } from './pages/CustomersPage';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';
import {
  QuotationFormRoute,
  QuotationsPage,
  QuotationWorkOrderCreateRoute,
} from './pages/QuotationsPage';
import { SampleInformationsPage } from './pages/SampleInformationsPage';
import {
  SamplingWorkOrdersPage,
  WorkOrderDetailRoute,
  WorkOrderEditRoute,
  WorkOrderSampleCreateRoute,
} from './pages/SamplingWorkOrdersPage';
import { RegisterPage } from './pages/RegisterPage';
import { SuperadminPermissionsPage } from './pages/SuperadminPermissionsPage';
import { SuperadminUserTypesPage } from './pages/SuperadminUserTypesPage';
import { SuperadminUsersPage } from './pages/SuperadminUsersPage';
import { UserDetailTabScreen } from './pages/SuperadminUsersPage/TabScreens/UserDetail';
import { MastersPage } from './pages/MastersPage';
import { MastersEntityCrudPage } from './pages/MastersEntityCrudPage';
import { ProfilePage } from './pages/ProfilePage';
import { SamplingSchedulesPage, SamplingScheduleDetailRoute } from './pages/SamplingSchedulesPage';
import { SamplingPage } from './pages/SurveyPage';
import { SurveyDetailRoute } from './pages/SurveyPage/SurveyDetailRoute';
import { SamplePreservationPage } from './pages/SamplePreservationPage';
import { LaboratoryResultsPage } from './pages/LaboratoryResultsPage';
import { HasilUjiLetterPage } from './pages/HasilUjiLetterPage';
import { LhuContentEditRoute } from './pages/HasilUjiLetterPage/LhuContentEditRoute';
import { InvoicesPage } from './pages/InvoicesPage';

/** Legacy `/sampling/work-order/:id` (tanpa /view) — pertahankan hash tab. */
function RedirectWorkOrderToView() {
  const { documentId } = useParams<{ documentId: string }>();
  const location = useLocation();
  if (!documentId) return <Navigate to="/sampling/work-orders" replace />;
  return (
    <Navigate
      to={`/sampling/work-order/${encodeURIComponent(documentId)}/view${location.search}${location.hash}`}
      replace
    />
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* <Route path="/register" element={<RegisterPage />} /> */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route element={<ProtectedLayout />}>
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="customers/new" element={<CustomerFormRoute />} />
        <Route path="customer/:documentId/view" element={<CustomerFormRoute />} />
        <Route path="customer/:documentId/edit" element={<CustomerFormRoute />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="quotations/new" element={<QuotationFormRoute />} />
        <Route path="quotation/:documentId/view" element={<QuotationFormRoute />} />
        <Route path="quotation/:documentId/edit" element={<QuotationFormRoute />} />
        <Route
          path="quotation/:documentId/work-order/new"
          element={<QuotationWorkOrderCreateRoute />}
        />
        <Route path="quotations" element={<QuotationsPage />} />
        <Route path="sampling/samples" element={<SampleInformationsPage />} />
        <Route path="sampling/test-results" element={<Navigate to="/sampling/work-orders" replace />} />
        <Route path="sampling/sample-informations" element={<Navigate to="/sampling/samples" replace />} />
        <Route path="sampling/work-orders" element={<SamplingWorkOrdersPage />} />
        <Route path="sampling/schedules" element={<SamplingSchedulesPage />} />
        <Route path="sampling/schedules/:documentId" element={<SamplingScheduleDetailRoute />} />
        <Route path="sampling/survey-schedule" element={<Navigate to="/sampling/schedules" replace />} />
        <Route path="sampling/pre-survey" element={<Navigate to="/sampling/schedules" replace />} />
        <Route path="sampling/survey" element={<Navigate to="/sampling/sampling" replace />} />
        <Route path="sampling/sampling" element={<SamplingPage />} />
        <Route path="sampling/sampling/:workOrderDocumentId" element={<SurveyDetailRoute />} />
        <Route path="sampling/sample-preservation" element={<SamplePreservationPage />} />
        <Route path="sampling/laboratory-results/*" element={<LaboratoryResultsPage />} />
        <Route path="sampling/hasil-uji-letter" element={<HasilUjiLetterPage />} />
        <Route path="sampling/hasil-uji-letter/:jobNumber" element={<LhuContentEditRoute />} />
        <Route path="keuangan/invoices" element={<InvoicesPage />} />
        <Route
          path="sampling/pre-survey/work-order/:documentId"
          element={<Navigate to="/sampling/schedules" replace />}
        />
        <Route path="sampling/work-order/:documentId" element={<RedirectWorkOrderToView />} />
        <Route path="sampling/work-order/:documentId/view" element={<WorkOrderDetailRoute />} />
        <Route path="sampling/work-order/:documentId/edit" element={<WorkOrderEditRoute />} />
        <Route path="sampling/work-order/:documentId/sample/new" element={<WorkOrderSampleCreateRoute />} />
        <Route path="marketing" element={<Navigate to="/dashboard" replace />} />
        <Route path="superadmin/users" element={<SuperadminUsersPage />} />
        <Route path="superadmin/users/:id/edit" element={<UserDetailTabScreen mode="edit" />} />
        <Route path="superadmin/users/:id/view" element={<UserDetailTabScreen mode="view" />} />
        <Route path="masters/customer-samples" element={<Navigate to="/customers" replace />} />
        <Route path="masters/quotation-logs" element={<Navigate to="/quotations" replace />} />
        <Route path="masters" element={<MastersPage />} />
        <Route path="masters/:entitySlug" element={<MastersEntityCrudPage />} />
        <Route path="master/:entitySlug" element={<MastersEntityCrudPage />} />
        <Route path="superadmin/permissions" element={<SuperadminPermissionsPage />} />
        <Route path="superadmin/user-types" element={<SuperadminUserTypesPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
